// Cookie consent store — framework free on purpose, so the analytics side
// effect modules (src/posthog.js, src/linkedin-insight.js) can consult it at
// import time, before React mounts.
//
// Categories
//   essential   always on. Language cookie, form submission, Turnstile, and the
//               cookieless Cloudflare page counter. No consent required.
//   analytics   PostHog product analytics (src/posthog.js).
//   marketing   LinkedIn Insight Tag (src/linkedin-insight.js), the Google
//               Ads tag and Google Tag Manager (src/google-ads-tag.js), and
//               the Reddit Pixel with its rdt_cid click-id cookie
//               (src/reddit-pixel.js).
//
// No stored decision means "not asked yet": hasConsent returns false and the
// banner is shown. Website PostHog separately permits memory-only capture
// before a choice; persistent analytics and marketing require opt-in. A decline
// stops browser analytics. Keep this distinction aligned with src/posthog.js
// and the approved privacy notice; storage choice alone is no legal exemption.
// The decision cookie lasts 180 days, after which the banner asks again.

export const CONSENT_COOKIE = 'sw_consent';
export const CONSENT_VERSION = 1;
export const OPTIONAL_CATEGORIES = ['analytics', 'marketing'];

const MAX_AGE_SECONDS = 60 * 60 * 24 * 180; // 180 days

const listeners = new Set();

/** A browser's opt-out signal wins over a previously saved marketing grant. */
export function hasGlobalPrivacyControl() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

// undefined = cookie not parsed yet · null = no decision on file · object = decision
let decision;

function readRawCookie(name) {
  if (typeof document === 'undefined') return null;
  const jar = document.cookie ? document.cookie.split(';') : [];
  for (const part of jar) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    return part.slice(eq + 1).trim();
  }
  return null;
}

function parseDecision(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    // A version bump invalidates old decisions: treat them as "not asked", which
    // re-shows the banner rather than silently assuming the old answer still
    // covers a category we have since added.
    if (!parsed || typeof parsed !== 'object' || parsed.v !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      at: typeof parsed.at === 'string' ? parsed.at : null,
    };
  } catch (_) {
    return null;
  }
}

/** The stored decision, or null when the visitor has not chosen yet. */
export function getConsent() {
  if (decision === undefined) decision = parseDecision(readRawCookie(CONSENT_COOKIE));
  return decision && hasGlobalPrivacyControl() ? { ...decision, marketing:false } : decision;
}

/** True once the visitor has made an explicit choice. */
export function hasDecision() {
  return getConsent() !== null;
}

/** May we run `category`? Essential is always allowed; everything else is opt in. */
export function hasConsent(category) {
  if (category === 'essential') return true;
  const current = getConsent();
  return current ? current[category] === true : false;
}

/** Persist a choice and notify subscribers. Returns the stored decision. */
export function saveConsent(prefs) {
  const next = {
    version: CONSENT_VERSION,
    analytics: prefs && prefs.analytics === true,
    marketing: !hasGlobalPrivacyControl() && prefs && prefs.marketing === true,
    at: new Date().toISOString(),
  };

  if (typeof document !== 'undefined') {
    try {
      const value = encodeURIComponent(JSON.stringify({
        v: CONSENT_VERSION,
        analytics: next.analytics,
        marketing: next.marketing,
        at: next.at,
      }));
      const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; secure' : '';
      document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; samesite=Lax${secure}`;
    } catch (_) { /* ignore */ }
  }

  decision = next;
  listeners.forEach((fn) => { try { fn(next); } catch (_) { /* ignore */ } });
  return next;
}

export function acceptAll() {
  return saveConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  return saveConsent({ analytics: false, marketing: false });
}

/**
 * Subscribe to consent changes. Analytics modules use this to start late when
 * someone accepts without reloading. Returns an unsubscribe function.
 */
export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when `next` withdraws a category that `previous` had granted. */
export function isWithdrawal(previous, next) {
  if (!previous) return false;
  return OPTIONAL_CATEGORIES.some((cat) => previous[cat] === true && next[cat] !== true);
}

/**
 * Best effort cleanup of first party analytics storage after a withdrawal.
 * Third party cookies set on vendor domains are outside our reach; those stop
 * being refreshed because the tag no longer loads.
 */
export function clearAnalyticsStorage() {
  if (typeof document === 'undefined') return;
  try {
    for (const part of (document.cookie || '').split(';')) {
      const name = part.split('=')[0].trim();
      if (!name || !name.startsWith('ph_')) continue;
      document.cookie = `${name}=; path=/; max-age=0; samesite=Lax`;
      // PostHog shares consented identity across the apex and its subdomains.
      // A host-only deletion cannot remove that domain-scoped cookie.
      const host = window.location.hostname;
      const base = ['scopeworth.app', 'getscopeworth.com'].find((domain) => host === domain || host.endsWith(`.${domain}`));
      if (base) document.cookie = `${name}=; domain=${base}; path=/; max-age=0; samesite=Lax`;
    }
  } catch (_) { /* ignore */ }
  for (const store of [safeStorage('localStorage'), safeStorage('sessionStorage')]) {
    if (!store) continue;
    try {
      // Collect first, remove after: removing during the walk reindexes the store.
      const doomed = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key && (key.startsWith('ph_') || key.startsWith('__ph'))) doomed.push(key);
      }
      doomed.forEach((key) => store.removeItem(key));
    } catch (_) { /* ignore */ }
  }
}

/** Stop refreshing ad identifiers; vendor-domain cookies are outside this site's reach. */
export function clearMarketingStorage() {
  if (typeof document === 'undefined') return;
  try {
    for (const part of (document.cookie || '').split(';')) {
      const name = part.split('=')[0].trim();
      if (!/^li_|^_li|^_gcl_|^_rdt_|^rdt_cid$/.test(name)) continue;
      document.cookie = `${name}=; path=/; max-age=0; samesite=Lax`;
      const host = window.location.hostname;
      const base = host.endsWith('scopeworth.app') ? 'scopeworth.app' : host.endsWith('getscopeworth.com') ? 'getscopeworth.com' : null;
      if (base) document.cookie = `${name}=; domain=${base}; path=/; max-age=0; samesite=Lax`;
    }
    for (const store of [safeStorage('localStorage'),safeStorage('sessionStorage')]) {
      if (!store) continue;
      const keys = [];
      for (let i=0;i<store.length;i++) { const key=store.key(i); if (/^li_|^_li|^_gcl_|^_rdt_|^rdt_cid$/.test(key || '')) keys.push(key); }
      keys.forEach(key => store.removeItem(key));
    }
  } catch (_) { /* best-effort storage cleanup */ }
}

// Private browsing and storage-blocked contexts throw on access, not on use.
function safeStorage(name) {
  try {
    return window[name] || null;
  } catch (_) {
    return null;
  }
}
